import { useState } from 'react'
import { Link } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import { api, type ReservationSecurityDeposit } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { formatCurrency } from '@/lib/format-utils'
import { disputeAwaitsEvidence, disputeLabel } from '@/components/protection/deposit-presentation'
import { useToast } from '@/hooks/use-toast'
import { ExtendDepositHoldDialog } from './extend-deposit-hold-dialog'
import { cn } from '@/lib/utils'

interface SecurityDepositCardProps {
  reservationId: string
  /**
   * Merged over the card's default width (65% from `md` up, which suits the
   * reservation page's wide column). The calendar's side sheet is already
   * narrow and passes `md:max-w-none`.
   */
  className?: string
}

type BadgeVariant = 'default' | 'success' | 'warning' | 'destructive' | 'secondary'

/**
 * How each lifecycle status reads to a human, and how urgent it looks.
 *
 * `captured` is styled as a warning rather than a success: money was taken
 * from a guest, which is worth the eye stopping on it even though it is a
 * legitimate outcome.
 */
const STATUS_DISPLAY: Record<string, { label: string; variant: BadgeVariant }> = {
  pending: { label: 'Pending', variant: 'secondary' },
  authorized: { label: 'Held', variant: 'success' },
  captured: { label: 'Charged', variant: 'warning' },
  released: { label: 'Released', variant: 'default' },
  failed: { label: 'Failed', variant: 'destructive' },
  skipped: { label: 'Not applicable', variant: 'secondary' },
  uplisting_managed: { label: 'Held by Uplisting', variant: 'secondary' },
}

/** Why no deposit could be placed, in the operator's terms rather than ours. */
const MATCH_OUTCOME_LABEL: Record<string, string> = {
  no_customer_found: 'No matching Stripe customer for this booking',
  ambiguous_match: 'More than one Stripe customer matches this booking',
  no_payment_method_found: 'No saved card on the Stripe customer',
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Security deposit state for one reservation.
 *
 * Read-only by design. Capturing a deposit for damages, or refunding one, is
 * done in the Stripe dashboard — the same place Uplisting's own Protect sends
 * hosts — so this card's job is to show where things stand and get you there
 * in one click, not to put an irreversible money-moving button in an admin UI.
 *
 * Renders nothing at all when a reservation has no deposit record, which is
 * the common case: the feature is off for most properties, and Airbnb
 * bookings can never have one.
 */
export function SecurityDepositCard({ reservationId, className }: SecurityDepositCardProps) {
  const { data, isLoading, error, refetch } = useQuery<ReservationSecurityDeposit>({
    queryKey: ['reservation-security-deposit', reservationId],
    queryFn: () => api.getReservationSecurityDeposit(reservationId),
    staleTime: 30000,
    enabled: !!reservationId,
  })

  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [extendOpen, setExtendOpen] = useState(false)
  const removeExtension = useMutation({
    mutationFn: () =>
      api.setDepositHoldUntil(reservationId, {
        hold_until: null,
        reason: 'Extension removed from the reservation page',
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['reservation-security-deposit', reservationId],
        }),
        queryClient.invalidateQueries({ queryKey: ['protection-deposits'] }),
      ])
      toast({ title: 'Extension removed', description: 'The hold is back on its normal release.' })
    },
    onError: (err: unknown) =>
      toast({
        title: "Couldn't remove the extension",
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      }),
  })

  if (isLoading) return <LoadingSkeleton className={cn('h-24 w-full md:max-w-[65%]', className)} />

  // A failed lookup is NOT the same as "this reservation has no deposit", and
  // must not be rendered as one: the hidden state could be a hold that failed
  // to place, or one still sitting on a guest's card. Staying silent here
  // would mask exactly the cases an operator needs to see.
  if (error) {
    return (
      <Card className={cn('w-full md:max-w-[65%]', className)}>
        <CardHeader className="pb-3">
          <CardTitle>Security deposit</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-textMuted">Couldn&apos;t load the deposit status.</p>
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Genuinely no deposit — the common case, since the feature is off for most
  // properties and Airbnb bookings can never have one.
  if (!data || data.status === 'none' || !data.deposit) return null

  const deposit = data.deposit
  const display = STATUS_DISPLAY[data.status] ?? { label: data.status, variant: 'secondary' }
  const isSimulated = data.mode === 'dry_run'
  const expiresAt = formatDate(deposit.authorization_expires_at)
  const refundedCents = deposit.refunded_amount_cents ?? null
  const disputeStatus = deposit.dispute_status ?? null
  // A lost dispute takes the charge back, and the disputed amount is not on the record, so
  // "kept" would overstate what the host has. Show only the refund then.
  const keptCents =
    refundedCents != null && deposit.captured_amount_cents != null && disputeStatus !== 'lost'
      ? Math.max(deposit.captured_amount_cents - refundedCents, 0)
      : null
  // admin-api keeps the deadline recorded at opening after a dispute closes, so it is only
  // shown while Stripe is still waiting on evidence.
  const disputeDueBy = disputeAwaitsEvidence(disputeStatus)
    ? formatDate(deposit.dispute_due_by)
    : null
  const holdUntil = formatDate(deposit.hold_until)
  const heldByUplisting = data.status === 'uplisting_managed'
  // A hold that can still be extended: one that is live or about to be, and not simulated.
  const canExtend =
    !isSimulated && ['authorized', 'uplisting_managed', 'pending', 'failed'].includes(data.status)
  const matchNote = deposit.match_outcome ? MATCH_OUTCOME_LABEL[deposit.match_outcome] : null

  return (
    <Card className={cn('w-full md:max-w-[65%]', className)}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="whitespace-nowrap">Security deposit</CardTitle>
          <Badge variant={display.variant} className="whitespace-nowrap">
            {display.label}
          </Badge>
          {/* Without this, a simulated decision would be indistinguishable
              from a real hold on the guest's card. */}
          {isSimulated && <Badge variant="secondary">Simulated</Badge>}
          {holdUntil && <Badge variant="default">Extended</Badge>}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-textMuted">Amount</span>
            <span className="font-medium text-sm">
              {formatCurrency(deposit.amount_cents / 100)}
            </span>
          </div>

          {deposit.captured_amount_cents != null && (
            <div className="flex justify-between items-center">
              {/* "Amount charged", not "Charged": the status badge already
                  reads "Charged", and two identical words meaning different
                  things (a state vs. a figure) is a stutter. */}
              <span className="text-sm text-textMuted">Amount charged</span>
              <span className="font-medium text-sm">
                {formatCurrency(deposit.captured_amount_cents / 100)}
              </span>
            </div>
          )}

          {refundedCents != null && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-textMuted">Refunded</span>
              <span className="font-medium text-sm">{formatCurrency(refundedCents / 100)}</span>
            </div>
          )}

          {keptCents != null && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-textMuted">Kept after refund</span>
              <span className="font-medium text-sm">{formatCurrency(keptCents / 100)}</span>
            </div>
          )}

          {disputeStatus && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-textMuted">Dispute</span>
              <span className="font-medium text-sm">{disputeLabel(disputeStatus)}</span>
            </div>
          )}

          {disputeDueBy && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-textMuted">Evidence due</span>
              <span className="font-medium text-xs text-textMuted">{disputeDueBy}</span>
            </div>
          )}

          {data.status === 'authorized' && expiresAt && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-textMuted">Hold expires</span>
              <span className="font-medium text-xs text-textMuted">{expiresAt}</span>
            </div>
          )}

          {heldByUplisting && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-textMuted">Held by</span>
              <span className="font-medium text-sm">Uplisting{holdUntil ? ', then us' : ''}</span>
            </div>
          )}

          {holdUntil && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-textMuted">Kept through</span>
              <span className="font-medium text-sm">{holdUntil}</span>
            </div>
          )}

          {holdUntil && deposit.hold_until_reservation_id && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-textMuted">Because of</span>
              <Link
                to={`/reservations/${encodeURIComponent(deposit.hold_until_reservation_id)}`}
                className="font-medium text-sm text-mint hover:underline"
              >
                Booking {deposit.hold_until_reservation_id}
              </Link>
            </div>
          )}

          {matchNote && (
            <div className="pt-2 border-t border-border mt-2">
              <p className="text-xs text-textMuted">{matchNote}</p>
            </div>
          )}

          {deposit.last_error && data.status === 'failed' && (
            <div className="pt-2 border-t border-border mt-2">
              <p className="text-xs text-danger">{deposit.last_error}</p>
            </div>
          )}

          {isSimulated && (
            <p className="text-xs text-textMuted pt-2 border-t border-border mt-2">
              Deposit automation is off, so no money has moved. This is what would have happened.
            </p>
          )}

          {canExtend && (
            <div className="pt-2 border-t border-border mt-2 flex flex-wrap items-center gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => setExtendOpen(true)}>
                {holdUntil ? 'Change' : 'Extend hold'}
              </Button>
              {holdUntil && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={removeExtension.isPending}
                  onClick={() => removeExtension.mutate()}
                >
                  Remove extension
                </Button>
              )}
            </div>
          )}

          {data.stripe_url && (
            <div className="pt-2 border-t border-border mt-2">
              <a
                href={data.stripe_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-mint hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 rounded-sm"
              >
                {/* Charging for damages, refunding and answering a dispute all
                    happen in Stripe; the API points this link at an open dispute
                    ahead of the payment. */}
                {deposit.dispute_id ? 'View dispute in Stripe' : 'View in Stripe'}
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            </div>
          )}
        </div>
      </CardContent>
      {canExtend && (
        <ExtendDepositHoldDialog
          reservationId={reservationId}
          deposit={deposit}
          open={extendOpen}
          onOpenChange={setExtendOpen}
        />
      )}
    </Card>
  )
}
